'use client'

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport } from 'ai'
import { Mic, MicOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Send, Copy, Check, User, Bot, Terminal, Code, ChevronDown, ChevronUp } from 'lucide-react' // Added ChevronDown, ChevronUp for collapsible UI
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import MCPServerManager, { 
  MCPServer, 
  loadMcpServersFromStorage, 
  saveMcpServersToStorage, 
  DEFAULT_MCP_SERVERS 
} from './components/MCPServerManager' 

interface APIChatProps {
  context?: Record<string, any>
  className?: string
}

const APIChat: React.FC<APIChatProps> = ({
  context = {},
  className
}) => {
  const [mcpServers, setMcpServersState] = useState<MCPServer[]>(DEFAULT_MCP_SERVERS)
  
  const setMcpServersWithStorage = useCallback((servers: MCPServer[] | ((prev: MCPServer[]) => MCPServer[])) => {
    setMcpServersState(prevServers => {
      const newServers = typeof servers === 'function' ? servers(prevServers) : servers
      saveMcpServersToStorage(newServers) 
      return newServers
    })
  }, [])

  const [mcpTools, setMcpTools] = useState<Record<string, any[]>>({})
  const [loadingTools, setLoadingTools] = useState<Record<string, boolean>>({})
  const [isVoiceSupported, setIsVoiceSupported] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const recognitionRef = useRef<any>(null)

  // Conversation persistence - load initial messages from localStorage
  const loadPersistedMessages = useCallback(() => {
    if (typeof window === 'undefined') return []
    try {
      const stored = localStorage.getItem('apiChat_messages')
      if (stored) {
        const parsedMessages = JSON.parse(stored)
        // Validate message format and return only valid messages
        return Array.isArray(parsedMessages) ? parsedMessages.filter((msg: any) => 
          msg && typeof msg === 'object' && msg.id && msg.role && msg.content
        ) : []
      }
    } catch (error) {
      console.warn('Failed to load persisted messages:', error)
    }
    return []
  }, [])

  // Save messages to localStorage
  const saveMessagesToStorage = useCallback((messages: any[]) => {
    if (typeof window === 'undefined') return
    try {
      // Only save essential message data to avoid storage bloat
      const messagesToSave = messages.map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        createdAt: msg.createdAt,
        // Don't save tool calls/results to keep storage lean
      }))
      localStorage.setItem('apiChat_messages', JSON.stringify(messagesToSave))
    } catch (error) {
      console.warn('Failed to save messages to storage:', error)
    }
  }, [])

  const { messages, sendMessage, status, stop } = useChat({
    transport: new DefaultChatTransport({
      api: '/api/ai/chat',
    }),
    onError: (error) => {
      console.error('Chat error:', error)
    },
    onToolCall: ({ toolCall }) => {
      console.log('Tool called:', toolCall)
    },
    onFinish: ({ message }) => {
      console.log('Message finished:', message)
      console.log('Message parts:', (message as any).parts)
      
      // Process tool results from message.parts (AI SDK v5 format)
      if ((message as any).parts && Array.isArray((message as any).parts)) {
        console.log('Processing tool results from message.parts:')
        ;(message as any).parts.forEach((part: any, index: number) => {
          console.log(`Part ${index}:`, part)
          console.log(`Part ${index} type:`, part.type)
          console.log(`Part ${index} state:`, part.state)
          console.log(`Part ${index} toolCallId:`, part.toolCallId)
          console.log(`Part ${index} input:`, part.input)
          console.log(`Part ${index} output:`, part.output)
          
          // Check for tool results in the new format: type starts with 'tool-' and has output
          if (part.type && part.type.startsWith('tool-') && part.state === 'output-available' && part.output) {
            console.log('Tool result found in parts:', part.output)
            const result = part.output
            
            if (result && typeof result === 'object' && result.triggerEvent) {
              console.log('Triggering visual event:', result.triggerEvent, result.eventData)
              const event = new CustomEvent(result.triggerEvent, {
                detail: result.eventData
              })
              window.dispatchEvent(event)
            }
          }
        })
      }
      
      // Legacy support: Handle tool results from toolInvocations (if present)
      const legacyToolCalls = (message as any).toolInvocations || []
      if (legacyToolCalls.length > 0) {
        console.log('Processing toolInvocations (legacy format):', legacyToolCalls)
        legacyToolCalls.forEach((invocation: any, index: number) => {
          console.log(`Tool invocation ${index}:`, invocation)
          if (invocation.state === 'result') {
            console.log('Tool result found:', invocation.result)
            const result = invocation.result
            if (result && typeof result === 'object' && result.triggerEvent) {
              console.log('Triggering visual event:', result.triggerEvent, result.eventData)
              const event = new CustomEvent(result.triggerEvent, {
                detail: result.eventData
              })
              window.dispatchEvent(event)
            }
          }
        })
      } else {
        console.log('No toolInvocations found in message (using parts format)')
      }
    },
  })

  // Load persisted messages on component mount and merge with current messages
  const [persistedMessages, setPersistedMessages] = useState<any[]>([])
  
  useEffect(() => {
    const loaded = loadPersistedMessages()
    setPersistedMessages(loaded)
  }, [loadPersistedMessages])

  // Save messages whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      saveMessagesToStorage(messages)
    }
  }, [messages, saveMessagesToStorage])

  // Combine persisted messages with current messages for display
  const allMessages = [...persistedMessages, ...messages]

  const isLoading = status === 'streaming' || status === 'submitted'

  // Manual input handling for AI SDK 5.0
  // AI SDK 5.0 pattern - manage input state manually (same as FlutterAIChat)
  const [input, setInput] = useState('')
  
  // State for collapsible tool bubbles - collapsed by default like FlutterChat
  const [collapsedTools, setCollapsedTools] = useState<Record<string, boolean>>({})

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (input.trim()) {
      sendMessage({ text: input }, { 
        body: { 
          mcpServers: mcpServers,
          context
        } 
      })
      setInput('')
    }
  }

  // Toggle tool bubble collapse state
  const toggleToolCollapse = (toolId: string) => {
    setCollapsedTools(prev => ({
      ...prev,
      [toolId]: !prev[toolId] // Default to collapsed (true) when first accessed
    }))
  }

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
    const storedServers = loadMcpServersFromStorage()
    setMcpServersState(storedServers) 
  }, [])

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SpeechRecognition) {
      return
    }

    const recognition = new SpeechRecognition()
    recognition.lang = 'en-US'
    recognition.interimResults = false
    recognition.maxAlternatives = 1

    recognition.onstart = () => {
      setIsListening(true)
    }

    recognition.onend = () => {
      setIsListening(false)
    }

    recognition.onerror = (event: any) => {
      console.error('Voice recognition error:', event)
      setIsListening(false)
    }

    recognition.onresult = (event: any) => {
      const results = event.results
      if (!results || results.length === 0) {
        return
      }

      const lastResult = results[results.length - 1]
      if (!lastResult.isFinal) {
        return
      }

      const transcript: string = (lastResult[0]?.transcript || '').trim()
      if (!transcript) {
        return
      }

      handleInputChange({
        target: {
          value: `${input ? `${input.trim()} ` : ''}${transcript}`.trim(),
        },
      } as any)

      if (inputRef.current) {
        inputRef.current.focus()
      }
    }

    recognitionRef.current = recognition
    setIsVoiceSupported(true)

    return () => {
      try {
        recognition.stop()
      } catch (error) {
        // ignore clean-up failures
      }
      recognitionRef.current = null
    }
  }, [handleInputChange, input])

  const fetchMcpTools = async (server: MCPServer) => {
    if (!server.enabled) return
    setLoadingTools(prev => ({ ...prev, [server.id]: true }))
    try {
      const response = await fetch('/api/ai/tools', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ server })
      })
      if (response.ok) {
        const { tools } = await response.json()
        setMcpTools(prev => ({ ...prev, [server.id]: tools || [] }))
      } else {
        console.error(`Failed to fetch tools for ${server.name}`)
        setMcpTools(prev => ({ ...prev, [server.id]: [] }))
      }
    } catch (error) {
      console.error(`Error fetching tools for ${server.name}:`, error)
      setMcpTools(prev => ({ ...prev, [server.id]: [] }))
    } finally {
      setLoadingTools(prev => ({ ...prev, [server.id]: false }))
    }
  }

  const [copiedStates, setCopiedStates] = useState<{[key: string]: boolean}>({})

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedStates(prev => ({ ...prev, [id]: true }))
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [id]: false }))
      }, 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  const renderCodeBlock = (content: string, id: string) => {
    const codeBlockRegex = /```([\w]*)?\n?([\s\S]*?)```/g
    const parts = []
    let lastIndex = 0
    let match

    while ((match = codeBlockRegex.exec(content)) !== null) {
      // Add text before code block
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: content.slice(lastIndex, match.index)
        })
      }
      
      // Add code block
      parts.push({
        type: 'code',
        language: match[1] || 'text',
        content: match[2].trim(),
        id: `${id}-code-${parts.length}`
      })
      
      lastIndex = match.index + match[0].length
    }
    
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.slice(lastIndex)
      })
    }
    
    return parts.length > 1 ? parts : [{ type: 'text', content }]
  }

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }, [handleSubmit])

  const handleSubmitWithStop = useCallback((event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    if (isListening) {
      try {
        recognitionRef.current?.stop()
      } catch (error) {
        console.error('Failed to stop recognition before submit:', error)
      }
    }
    handleSubmit(event)
  }, [handleSubmit, isListening])

  const handleVoiceToggle = useCallback(() => {
    if (!isVoiceSupported) {
      return
    }

    const recognition = recognitionRef.current
    if (!recognition) {
      return
    }

    try {
      if (isListening) {
        recognition.stop()
      } else {
        recognition.start()
      }
    } catch (error) {
      console.error('Voice recognition toggle failed:', error)
      setIsListening(false)
    }
  }, [isVoiceSupported, isListening])

  const transformedMessages = [
    ...(isClient ? [{
      id: 'welcome',
      type: 'system',
      content: 'Welcome to your AI Trading Assistant! I can help you with trading, portfolio analysis, technical analysis, and managing your trading interface. What would you like me to help you with?',
      timestamp: new Date('2024-01-01T00:00:00.000Z')
    }] : []),
    ...allMessages.map((msg: any) => {
      // Extract tool calls and results from message.parts (new AI SDK format)
      const toolParts = msg.parts?.filter((p: any) => p.type?.startsWith('tool-')) || []
      
      // Extract tool calls - look for parts with toolCallId and input
      const toolCalls = toolParts
        .filter((p: any) => p.toolCallId && p.input)
        .map((p: any) => ({
          id: p.toolCallId,
          name: p.type?.replace('tool-', '') || 'unknown',
          parameters: p.input || {}
        }))
      
      // Extract tool results - look for parts with output and state 'output-available'
      const toolResults = toolParts
        .filter((p: any) => p.state === 'output-available' && p.output)
        .map((p: any) => ({
          toolCallId: p.toolCallId,
          result: p.output,
          success: true // Assume success if we have output
        }))

      // Legacy support for toolInvocations (fallback)
      const legacyToolCalls = (msg as any).toolInvocations?.map((tool: any) => ({
        id: tool.toolCallId || `tool_${Date.now()}`,
        name: tool.toolName,
        parameters: tool.args || {}
      })) || []

      const legacyToolResults = (msg as any).toolInvocations?.filter((tool: any) => tool.state === 'result').map((tool: any) => ({
        toolCallId: tool.toolCallId || `tool_${Date.now()}`,
        result: tool.result,
        success: true 
      })) || []

      return {
        id: msg.id,
        type: msg.role === 'user' ? 'user' : 'assistant',
        content: msg.content || (msg.parts && msg.parts.find((p: any) => p.type === 'text')?.text) || '',
        timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
        toolCalls: [...toolCalls, ...legacyToolCalls],
        toolResults: [...toolResults, ...legacyToolResults]
      }
    })
  ]

  const formatMessage = (message: any) => {
    const messageId = `${message.id}-${message.type}`
    
    switch (message.type) {
      case 'system':
        return (
          <div className="mb-6 w-full">
            <div className="flex flex-col items-start mb-2">
              <div className="w-8 h-8 rounded-full bg-[#88a1ac33] flex items-center justify-center">
                <Terminal className="w-4 h-4 text-[#21728d]" />
              </div>
            </div>
            <Card className="bg-[#88a1ac33] border-[#88a1ac77] w-full">
              <div className="p-4">
                <div className="text-sm text-[#88a1ac] whitespace-pre-wrap">
                  {message.content}
                </div>
              </div>
            </Card>
            {isClient && (
              <div className="text-xs text-muted-foreground mt-2 text-center">
                {message.timestamp.toLocaleTimeString()}
              </div>
            )}
          </div>
        )
      case 'user':
        return (
          <div className="mb-6 w-full">
            <div className="flex flex-col items-start mb-2">
              <div className="w-8 h-8 rounded-full bg-[#88a1ac33] flex items-center justify-center">
                <User className="w-4 h-4 text-[#88a1ac]" />
              </div>
            </div>
            <Card className="bg-[#88a1ac18] border-[#88a1ac33] w-full">
              <div className="p-4">
                <div className="text-sm whitespace-pre-wrap">
                  {message.content}
                </div>
              </div>
            </Card>
            {isClient && (
              <div className="text-xs text-muted-foreground mt-2 text-center">
                {message.timestamp.toLocaleTimeString()}
              </div>
            )}
          </div>
        )
      case 'assistant':
        const contentParts = renderCodeBlock(message.content, messageId)
        const isLastMessage = transformedMessages[transformedMessages.length - 1]?.id === message.id
        const isStreaming = isLastMessage && isLoading
        
        return (
          <div className="mb-6 w-full">
            <div className="flex flex-col items-start mb-2">
              <div className="w-8 h-8 rounded-full bg-[#88a1ac33] flex items-center justify-center">
                <Bot className="w-4 h-4 text-[#21728d]" />
              </div>
            </div>
            <Card className="bg-card border-border w-full">
              <div className="p-4">
                <div className="text-sm text-foreground">
                  {contentParts.map((part, index) => {
                    if (part.type === 'code') {
                      return (
                        <div key={index} className="my-4 first:mt-0 last:mb-0">
                          <div className="relative">
                            <div className="flex items-center justify-between bg-muted/50 px-4 py-2 rounded-t-lg border border-b-0">
                              <div className="flex items-center gap-2">
                                <Code className="w-4 h-4 text-muted-foreground" />
                                <span className="text-xs font-medium text-muted-foreground">
                                  {part.language || 'text'}
                                </span>
                              </div>
                              <button
                                onClick={() => copyToClipboard(part.content, part.id || `code-${index}`)}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-background hover:bg-muted rounded transition-colors"
                              >
                                {copiedStates[part.id || `code-${index}`] ? (
                                  <><Check className="w-3 h-3" /> Copied</>
                                ) : (
                                  <><Copy className="w-3 h-3" /> Copy</>
                                )}
                              </button>
                            </div>
                            <div className="bg-muted/30 border rounded-b-lg p-4 overflow-x-auto">
                              <pre className="text-sm font-mono text-foreground whitespace-pre-wrap">
                                <code>{part.content}</code>
                              </pre>
                            </div>
                          </div>
                        </div>
                      )
                    } else {
                      return (
                        <div key={index} className="whitespace-pre-wrap">
                          {part.content}
                          {/* Show streaming cursor only on the last part of the last message */}
                          {isStreaming && index === contentParts.length - 1 && (
                            <span className="streaming-cursor animate-pulse">|</span>
                          )}
                        </div>
                      )
                    }
                  })}
                </div>
              </div>
            </Card>
            
            {message.toolCalls && message.toolCalls.length > 0 && (
              <div className="mt-3 space-y-2 w-full">
                {message.toolCalls.map((call: any) => {
                  const isCollapsed = collapsedTools[call.id] ?? true // Default to collapsed
                  const hasResult = message.toolResults?.find((result: any) => result.toolCallId === call.id)
                  
                  return (
                    <Card key={call.id} className="bg-muted/30 border-muted w-full">
                      <div className="p-3">
                        {/* Collapsible header - always visible */}
                        <div 
                          className="flex items-center justify-between cursor-pointer hover:bg-muted/20 rounded p-2 -m-2"
                          onClick={() => toggleToolCollapse(call.id)}
                        >
                          <div className="flex items-center gap-2">
                            <Terminal className="w-4 h-4 text-[#21728d]" />
                            <span className="text-sm font-medium text-[#21728d]">
                              {call.name}
                            </span>
                            {hasResult && (
                              <div className={`w-2 h-2 rounded-full ${
                                hasResult.success ? 'bg-green-500' : 'bg-red-500'
                              }`} />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {hasResult && (
                              <span className={`text-xs px-2 py-1 rounded ${
                                hasResult.success 
                                  ? 'bg-green-500/10 text-green-600' 
                                  : 'bg-red-500/10 text-red-600'
                              }`}>
                                {hasResult.success ? 'Success' : 'Error'}
                              </span>
                            )}
                            {isCollapsed ? (
                              <ChevronDown className="w-4 h-4 text-muted-foreground" />
                            ) : (
                              <ChevronUp className="w-4 h-4 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                        
                        {/* Collapsible content - hidden when collapsed */}
                        {!isCollapsed && (
                          <>
                            <div className="bg-background/50 rounded p-3 mt-3 mb-2">
                              <div className="text-xs text-muted-foreground mb-1">Parameters:</div>
                              <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                                {JSON.stringify(call.parameters, null, 2)}
                              </pre>
                            </div>
                            
                            {hasResult && (
                              <div className={`rounded p-3 ${
                                hasResult.success
                                  ? 'bg-green-500/10 border border-green-500/20' 
                                  : 'bg-red-500/10 border border-red-500/20'
                              }`}>
                                <div className="text-xs text-muted-foreground mb-1">Result:</div>
                                <pre className={`text-xs font-mono whitespace-pre-wrap ${
                                  hasResult.success ? 'text-green-600' : 'text-red-400'
                                }`}>
                                  {(() => {
                                    const resultData = hasResult.result
                                    if (resultData && typeof resultData === 'object' && resultData.type && resultData.message) {
                                      return resultData.message
                                    }
                                    return typeof resultData === 'string' ? resultData : JSON.stringify(resultData, null, 2)
                                  })()}
                                </pre>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </Card>
                  )
                })}
              </div>
            )}
            
            {isClient && (
              <div className="text-xs text-muted-foreground mt-2 text-center">
                {message.timestamp.toLocaleTimeString()}
              </div>
            )}
          </div>
        )
      default:
        return <div>Unknown message type</div>
    }
  }

  if (!isClient) {
    return (
      <div className={cn("flex flex-col h-full", className)}>
        <div className="flex-1 overflow-y-auto p-3 min-h-0">
          <div className="flex justify-center items-center h-full">
            <div className="text-sidebar-foreground/50">Loading AI Chat...</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("flex flex-col h-full", className)}>
      <div className="flex-1 overflow-y-auto p-3 min-h-0" style={{ scrollBehavior: 'smooth' }}>
        {/* Context Information */}
        {context.contextInfo && (
          <div className="mb-6 w-full">
            <Card className="bg-[#88a1ac33] border-[#88a1ac77] w-full">
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Terminal className="w-4 h-4 text-[#21728d]" />
                  <span className="text-sm font-medium text-[#21728d]">Context</span>
                </div>
                <div className="text-sm text-[#88a1ac] space-y-1">
                  <div>
                    Symbol: <span className="font-medium">{context.contextInfo.currentSymbol}</span>
                  </div>
                  <div>
                    Tools: <span className="font-medium">{context.contextInfo.toolsCount}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
        
        {transformedMessages.map((message) => (
          <div key={message.id} className="message-wrapper w-full">
            {formatMessage(message)}
          </div>
        ))}
        {isLoading && (
          <div className="mb-6 w-full">
            <div className="flex flex-col items-start mb-2">
              <div className="w-8 h-8 rounded-full bg-[#88a1ac33] flex items-center justify-center">
                <Bot className="w-4 h-4 text-[#21728d]" />
              </div>
            </div>
            <Card className="bg-card border-border w-full">
              <div className="p-4">
                <div className="flex items-center gap-2 justify-center">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className="text-sm text-muted-foreground">AI is thinking...</span>
                </div>
              </div>
            </Card>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-3 border-t border-border bg-background">
        <div className="flex gap-3 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="Ask me to analyze your portfolio, place trades, check RSI, or anything else..."
            disabled={isLoading}
            rows={2}
            className="flex-1 bg-sidebar-accent/50 border border-sidebar-border rounded-lg px-3 py-2 text-black text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#88a1ac77]"
          />
          <button
            type="button"
            onClick={handleVoiceToggle}
            disabled={!isVoiceSupported || isLoading}
            title={isVoiceSupported ? (isListening ? 'Stop voice input' : 'Start voice input') : 'Voice input not supported in this browser'}
            aria-pressed={isListening}
            className={`h-14 w-12 rounded-lg border border-sidebar-border flex items-center justify-center transition-colors ${isVoiceSupported ? (isListening ? 'bg-[#21728d] text-white' : 'bg-sidebar-accent/50 text-[#21728d] hover:bg-sidebar-accent/80') : 'bg-sidebar-accent/30 text-sidebar-foreground/40 cursor-not-allowed'}`}
          >
            {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <button 
            onClick={handleSubmitWithStop} 
            disabled={!input.trim() || isLoading}
            className="h-14 w-12 rounded-lg bg-[#21728d] hover:bg-[#218999] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
          >
            {isLoading ? '⏳' : '🚀'}
          </button>
        </div>
        
        <div className="flex flex-wrap gap-1 mt-2 items-center">
          <MCPServerManager 
            mcpServers={mcpServers}
            setMcpServers={setMcpServersWithStorage} 
            fetchMcpTools={fetchMcpTools}
            mcpTools={mcpTools}
            loadingTools={loadingTools}
            isChatLoading={isLoading}
          />
          
          <Button variant="outline" size="sm" className="text-xs h-6 px-2" onClick={() => handleInputChange({ target: { value: 'Analyze my current holdings and suggest rebalancing' } } as any)} disabled={isLoading}>
            Portfolio
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-6 px-2" onClick={() => handleInputChange({ target: { value: 'Show me stocks with RSI below 30' } } as any)} disabled={isLoading}>
            RSI Check
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-6 px-2" onClick={() => handleInputChange({ target: { value: 'Buy 10 shares of each symbol in my watchlist' } } as any)} disabled={isLoading}>
            Watchlist
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-6 px-2" onClick={() => handleInputChange({ target: { value: 'Place a buy order for 0.5 BTCUSDT at market price' } } as any)} disabled={isLoading}>
            Buy Demo
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-6 px-2" onClick={() => handleInputChange({ target: { value: 'Place a sell order for 1000 ETHUSDT' } } as any)} disabled={isLoading}>
            Sell Demo
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-6 px-2" onClick={() => handleInputChange({ target: { value: 'Place a limit buy order for 2 ETH at price 3000' } } as any)} disabled={isLoading}>
            Limit Order
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-6 px-2" onClick={() => handleInputChange({ target: { value: 'Create a note titled "Daily Trading Strategy"' } } as any)} disabled={isLoading}>
            📝 New Note
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-6 px-2" onClick={() => handleInputChange({ target: { value: 'Read my trading strategy note' } } as any)} disabled={isLoading}>
            👁️ Read Note
          </Button>
        </div>
      </div>

      <style>{`
        .typing-indicator {
          display: flex;
          gap: 4px;
          align-items: center;
        }
        .typing-indicator span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: hsl(var(--muted-foreground));
          animation: typing 1.4s ease-in-out infinite both;
        }
        .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
        .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }
        .typing-indicator span:nth-child(3) { animation-delay: 0s; }
        
        .streaming-cursor {
          color: hsl(var(--primary));
          font-weight: bold;
          margin-left: 2px;
          animation: blink 1s infinite;
        }
        
        @keyframes typing {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        @keyframes blink {
          0%, 50% {
            opacity: 1;
          }
          51%, 100% {
            opacity: 0;
          }
        }
      `}</style>
    </div>
  )
}

export default APIChat


